/**
 * Patient classification. api-user-service models/patient.go:36-42.
 *
 * NOTE this is a user classification, not a subscription tier — there is no
 * plan/tier field on the patient anywhere. PREMIUM_USER sits next to QA_USER and
 * SENSITIVE_USER, which is a hint about what it really means. A different
 * `isPremium` boolean exists on GET /v1/patients/{id}/settings (a feature-flag
 * blob on another service); the two are not interchangeable.
 */
export const PROFILE_TYPE = Object.freeze({
  DEFAULT: 0,
  PREMIUM_USER: 1,
  HR_REPRESENTATIVE: 2,
  QA_USER: 3,
  SENSITIVE_USER: 4,
});

/**
 * Display names, in the order the filter offers them: the four special classes
 * first and DEFAULT last, which is bridge's order too (UserTypeFilter.js:6).
 *
 * NB Default is 0, not 5. It is the profile type's own encoding rather than a
 * position in this list, and reading it off the end of the list is the obvious
 * way to get this wrong.
 */
export const PROFILE_TYPE_OPTIONS = Object.freeze([
  { id: PROFILE_TYPE.PREMIUM_USER, label: "Premium user" },
  { id: PROFILE_TYPE.HR_REPRESENTATIVE, label: "HR representative" },
  { id: PROFILE_TYPE.QA_USER, label: "QA user" },
  { id: PROFILE_TYPE.SENSITIVE_USER, label: "Sensitive user" },
  { id: PROFILE_TYPE.DEFAULT, label: "Default user" },
]);

/**
 * A row's type as words, or "" when the row carries no type at all.
 *
 * ABSENT IS NOT DEFAULT. Coercing a missing value to 0 would announce and
 * tooltip the row as "Default user", which is a positive claim the data does not
 * make — and now a visible contradiction, since Default is separately
 * filterable: a row labelled "Default user" that a `profileTypes: [0]` filter
 * does not return. Bridge guards the same way (NavigoDashboard.js:1624 checks
 * `!== undefined`). The avatar still falls back to the neutral grey.
 */
/**
 * The chip modifier a type earns on the patient header (Figma "Tag · User type",
 * 3015:4411).
 *
 * WHY THESE COLOURS. The board draws only the PREMIUM variant, so the other
 * four had to come from somewhere. They are not invented: phr already
 * colour-codes all five types on the appointments dashboard avatar
 * (appointments.scss:289-308), and bridge's user-type filter pills carry the
 * SAME four hexes — and that filter's premium entry is literally this board's
 * gradient stops (UserTypeFilter.js:6-19). Two independent screens already
 * agreeing is a stronger source than a fourth palette invented here.
 *
 * DEFAULT(0) IS CHIPPED, ABSENT IS NOT. These look like the same case and are
 * not. Default is a real, server-stored classification — the column is
 * `not null default 0`, it is separately filterable, and a header that showed
 * a chip for the four special classes and nothing for the ordinary one left the
 * commonest patient looking like a patient whose type had failed to load.
 * A MISSING profileType still earns nothing, because that is an absence of
 * data rather than a value, and coercing it to 0 would state a classification
 * the record does not make (the same line profileTypeLabel draws, and bridge
 * guards at NavigoDashboard.js:1624).
 */
export const PROFILE_TYPE_CHIP_CLASS = Object.freeze({
  [PROFILE_TYPE.PREMIUM_USER]: "is-premium",
  [PROFILE_TYPE.HR_REPRESENTATIVE]: "is-hr",
  [PROFILE_TYPE.QA_USER]: "is-qa",
  [PROFILE_TYPE.SENSITIVE_USER]: "is-sensitive",
  [PROFILE_TYPE.DEFAULT]: "is-default",
});

/**
 * The same five types, named WITHOUT the "user" suffix — "QA", not "QA user".
 *
 * For the appointment-details card's "User type" field and nothing else
 * (Nittin, 2026-09-12): that field is already labelled "User type", so the
 * suffix only said it twice. The filter, the dashboard cell and the patient
 * header chip keep PROFILE_TYPE_OPTIONS' wording — deliberately, because those
 * labels are bridge's too and a filter option reads as a noun on its own.
 */
const PROFILE_TYPE_SHORT_LABEL = Object.freeze({
  [PROFILE_TYPE.PREMIUM_USER]: "Premium",
  [PROFILE_TYPE.HR_REPRESENTATIVE]: "HR representative",
  [PROFILE_TYPE.QA_USER]: "QA",
  [PROFILE_TYPE.SENSITIVE_USER]: "Sensitive",
  [PROFILE_TYPE.DEFAULT]: "Default",
});

/** "" for an absent or unmapped type, so the caller can em-dash it. */
export const profileTypeShortLabel = (type) => {
  if (type === null || type === undefined || type === "") return "";
  const hit = PROFILE_TYPE_SHORT_LABEL[Number(type)];
  return hit || "";
};

export const profileTypeLabel = (type) => {
  if (type === null || type === undefined || type === "") return "";
  const hit = PROFILE_TYPE_OPTIONS.find((o) => o.id === Number(type));
  return hit ? hit.label : "";
};

export default PROFILE_TYPE;
